import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Search, FolderTree, ChevronRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { categoriesAPI } from '@/services/api';
import { cn } from '@/lib/utils';

const CategoryItem = ({ category, level = 0, onEdit, onDelete, expanded, onToggle }) => {
  const hasChildren = category.children && category.children.length > 0;
  const isExpanded = expanded.includes(category.id);

  return (
    <div>
      <div
        className={cn(
          "flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors",
          level > 0 && "ml-6"
        )}
        style={{ marginLeft: level * 24 }}
      >
        <div className="flex items-center gap-2">
          {hasChildren ? (
            <button onClick={() => onToggle(category.id)} className="text-gray-500 hover:text-gray-800">
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          ) : (
            <span className="w-4" />
          )}
          <FolderTree className="w-4 h-4 text-blue-500" />
          <span className="text-gray-800 font-medium">{category.name}</span>
          <span className="text-gray-500 text-sm">({category.code})</span>
          <Badge variant="outline" className="border-gray-300 text-gray-500 text-xs ml-2">
            Level {category.level}
          </Badge>
          {!category.is_active && (
            <Badge variant="secondary">Inactive</Badge>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-gray-800 hover:bg-gray-50"
            onClick={() => onEdit(category)}>
            <Pencil className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
            onClick={() => onDelete(category)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      {hasChildren && isExpanded && (
        <div>
          {category.children.map(child => (
            <CategoryItem
              key={child.id}
              category={child}
              level={level + 1}
              onEdit={onEdit}
              onDelete={onDelete}
              expanded={expanded}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({ code: '', name: '', parent_id: '', description: '' });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const fetchCategories = useCallback(async () => {
    try {
      const [listRes, treeRes] = await Promise.all([
        categoriesAPI.list({ search }),
        categoriesAPI.tree()
      ]);
      setCategories(listRes.data);
      setCategoryTree(treeRes.data);
      // Auto-expand first level
      setExpanded(treeRes.data.map(c => c.id));
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch categories', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [search, toast]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const toggleExpand = (id) => {
    setExpanded(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handleOpenDialog = async (category = null) => {
    if (category) {
      setSelectedCategory(category);
      setFormData({ code: category.code, name: category.name, parent_id: category.parent_id || '', description: category.description || '' });
    } else {
      setSelectedCategory(null);
      // Auto-generate code for new category
      try {
        const nextCode = await categoriesAPI.getNextCode();
        setFormData({ code: nextCode.code, name: '', parent_id: '', description: '' });
      } catch (error) {
        console.error('Error getting next code:', error);
        setFormData({ code: '', name: '', parent_id: '', description: '' });
      }
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast({ title: 'Validation Error', description: 'Name is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData, parent_id: formData.parent_id || null };
      if (selectedCategory) {
        await categoriesAPI.update(selectedCategory.id, payload);
        toast({ title: 'Success', description: 'Category updated successfully' });
      } else {
        await categoriesAPI.create(payload);
        toast({ title: 'Success', description: 'Category created successfully' });
      }
      setDialogOpen(false);
      fetchCategories();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save category', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await categoriesAPI.delete(selectedCategory.id);
      toast({ title: 'Success', description: 'Category deactivated successfully' });
      setDeleteDialogOpen(false);
      fetchCategories();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to delete category', variant: 'destructive' });
    }
  };

  // Get flat list for parent selection (exclude self and children)
  const getParentOptions = () => {
    if (!selectedCategory) return categories.filter(c => c.is_active);
    const excludeIds = new Set([selectedCategory.id]);
    // TODO: Also exclude all descendants
    return categories.filter(c => c.is_active && !excludeIds.has(c.id));
  };

  return (
    <div className="p-6 lg:p-8">
      <Toaster />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Categories</h1>
          <p className="text-gray-500">Manage product categories hierarchy</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-blue-500 hover:bg-blue-600" data-testid="add-category-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Category
        </Button>
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input placeholder="Search categories..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white border-gray-200 text-gray-800" />
        </div>
      </div>

      <Card className="bg-white border-gray-200">
        <CardContent className="p-4">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : categoryTree.length === 0 ? (
            <div className="text-center py-12">
              <FolderTree className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No categories found</p>
            </div>
          ) : (
            <div className="space-y-1">
              {categoryTree.map(category => (
                <CategoryItem
                  key={category.id}
                  category={category}
                  onEdit={handleOpenDialog}
                  onDelete={(cat) => { setSelectedCategory(cat); setDeleteDialogOpen(true); }}
                  expanded={expanded}
                  onToggle={toggleExpand}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-800">
          <DialogHeader>
            <DialogTitle>{selectedCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-600">Code *</Label>
                <Input value={formData.code} onChange={(e) => setFormData(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                  placeholder="e.g., ELEC" className="bg-gray-50 border-gray-300" disabled={!!selectedCategory} />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-600">Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="Category Name" className="bg-gray-50 border-gray-300" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-600">Parent Category</Label>
              <Select value={formData.parent_id || "none"} onValueChange={(value) => setFormData(p => ({ ...p, parent_id: value === "none" ? "" : value }))}>
                <SelectTrigger className="bg-gray-50 border-gray-300">
                  <SelectValue placeholder="None (Root Category)" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="none">None (Root Category)</SelectItem>
                  {getParentOptions().map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {'—'.repeat(Math.max(0, cat.level))} {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-600">Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                placeholder="Category description" className="bg-gray-50 border-gray-300" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-gray-300 text-gray-600">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-500 hover:bg-blue-600">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-800">Deactivate Category?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              This will deactivate "{selectedCategory?.name}". Sub-categories must be deactivated first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300 text-gray-600">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CategoriesPage;
